// src/context/PermissionContext.tsx
//
// ─── HOW TO SETUP (one-time) ────────────────────────────────────────────────
//
// 1. Wrap your app in App.tsx:
//    import { PermissionProvider } from './context/PermissionContext';
//    <PermissionProvider>
//      <Router> ... </Router>
//    </PermissionProvider>
//
// 2. In any admin page, use the hook:
//    import { usePermission } from '../../context/PermissionContext';
//    const { canWrite } = usePermission('product_management');
//    {canWrite && <Button>Add</Button>}
//    {canWrite && <IconButton><EditIcon /></IconButton>}
//    {canWrite && <IconButton><DeleteIcon /></IconButton>}
//
// ────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useMemo } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface PagePermission {
  canView:  boolean;
  canWrite: boolean;
}

interface PermissionContextValue {
  getPermission: (menuId: string) => PagePermission;
}

// ─── Context ─────────────────────────────────────────────────────────────────
const PermissionContext = createContext<PermissionContextValue>({
  getPermission: () => ({ canView: true, canWrite: true }),
});

// ─── Helper: build full permission map from localStorage ─────────────────────
function buildPermMap(): Record<string, PagePermission> {
  const rawRole = (
    localStorage.getItem('admin_type') ||
    localStorage.getItem('user_role')  ||
    'admin'
  ).toLowerCase().trim();

  const isSuperAdmin = rawRole.includes('super');
  const isOperation  = rawRole === 'operation';
  const isVendor     = rawRole === 'vendor';

  // Super admin → everything true
  if (isSuperAdmin) {
    return new Proxy({} as Record<string, PagePermission>, {
      get: () => ({ canView: true, canWrite: true }),
    });
  }

  // ── PRIORITY 1: Use pre-resolved perm map saved by sidemenu ──────────────
  // adminSlidemenu.tsx saves 'admin_perm_map' after resolving all permissions.
  // This is the most accurate source — already normalized to { canView, canWrite }.
  // Covers vendor too, since adminSlidemenu.tsx now resolves vendor's map from
  // the "Vendor" role in Role Master exactly like any other custom role.
  const resolvedRaw = localStorage.getItem('admin_perm_map');
  if (resolvedRaw && resolvedRaw !== 'null' && resolvedRaw !== 'undefined') {
    try {
      const resolved = JSON.parse(resolvedRaw) as Record<string, PagePermission>;
      if (Object.keys(resolved).length > 0) return resolved;
    } catch { /* fallthrough */ }
  }

  // ── PRIORITY 2: Operation fixed permissions ───────────────────────────────
  if (isOperation) {
    const allowed = ['product_management', 'order_management', 'bulk_order_management'];
    const map: Record<string, PagePermission> = {};
    allowed.forEach((id) => { map[id] = { canView: true, canWrite: true }; });
    return new Proxy(map, {
      get: (t, menuId: string) => t[menuId] ?? { canView: false, canWrite: false },
    });
  }

  // ── PRIORITY 3: Vendor fallback (only reached if admin_perm_map above
  // wasn't populated yet, e.g. very first render before SlideMenu mounts) ──
  // Role-driven access lives in admin_permissions, parsed below in PRIORITY 4.
  // If nothing is saved there either, default to Product Management only —
  // the historical safe default — rather than granting everything.
  if (isVendor) {
    const savedRaw = localStorage.getItem('admin_permissions');
    if (!savedRaw || savedRaw === 'null' || savedRaw === 'undefined') {
      const map: Record<string, PagePermission> = { product_management: { canView: true, canWrite: true } };
      return new Proxy(map, {
        get: (t, menuId: string) => t[menuId] ?? { canView: false, canWrite: false },
      });
    }
    // fall through to PRIORITY 4, which parses admin_permissions generically
  }

  // ── PRIORITY 4: Parse admin_permissions (raw login permissions) ───────────
  const savedRaw = localStorage.getItem('admin_permissions');

  // No saved permissions → plain admin, full access
  if (!savedRaw || savedRaw === 'null' || savedRaw === 'undefined') {
    return new Proxy({} as Record<string, PagePermission>, {
      get: () => ({ canView: true, canWrite: true }),
    });
  }

  try {
    const perms = JSON.parse(savedRaw) as Record<string, string | string[]>;
    const map: Record<string, PagePermission> = {};

    Object.entries(perms).forEach(([menuId, rights]) => {
      const arr: string[] = Array.isArray(rights)
        ? rights.map((r) => r.trim().toLowerCase())
        : String(rights).split(',').map((r) => r.trim().toLowerCase());

      map[menuId] = {
        canView:  arr.includes('view'),
        canWrite: arr.some((r) => ['add', 'edit', 'delete', 'write'].includes(r)),
      };
    });

    return map;
  } catch {
    return new Proxy({} as Record<string, PagePermission>, {
      get: () => ({ canView: true, canWrite: true }),
    });
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────
export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Build once per render (localStorage doesn't change mid-session)
  const permMap = useMemo(() => buildPermMap(), []);

  const getPermission = (menuId: string): PagePermission => {
    const p = permMap[menuId];
    // If menuId not found in map → no access
    if (p === undefined) {
      // For roles with Proxy (super_admin, no-perm admin), this won't happen
      // For custom roles, missing key = no access
      const savedRaw = localStorage.getItem('admin_permissions');
      if (!savedRaw || savedRaw === 'null') return { canView: true, canWrite: true };
      return { canView: false, canWrite: false };
    }
    return p;
  };

  return (
    <PermissionContext.Provider value={{ getPermission }}>
      {children}
    </PermissionContext.Provider>
  );
};

// ─── Hook ────────────────────────────────────────────────────────────────────
/**
 * usePermission('product_management')
 * Returns { canView, canWrite } for this page.
 *
 * canWrite = false → hide Add / Edit / Delete buttons
 * canView  = false → page should not be accessible (ProtectedRoute handles this)
 */
export function usePermission(menuId: string): PagePermission {
  const { getPermission } = useContext(PermissionContext);
  return useMemo(() => getPermission(menuId), [menuId, getPermission]);
}